import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { matterApi, type InterlocutoryChild } from '../../api';
import AddInterlocutoryModal from './AddInterlocutoryModal';

type Props = {
  matterId: number | string;
  parentMatterTypeCode?: string;
  mediationReferredAt?: string | null;
  mediationJointMinuteAt?: string | null;
  onChange?: () => void;
};

export default function InterlocutoriesPanel({
  matterId, parentMatterTypeCode,
  mediationReferredAt, mediationJointMinuteAt,
  onChange,
}: Props) {
  const [children, setChildren] = useState<InterlocutoryChild[]>([]);
  const [loading, setLoading] = useState(true);
  const [picking, setPicking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await matterApi.listInterlocutories(matterId);
      setChildren(r.interlocutories || []);
    } finally { setLoading(false); }
  }, [matterId]);

  useEffect(() => { load(); }, [load]);

  const mediationActive = !!mediationReferredAt && !mediationJointMinuteAt;

  return (
    <div className="stack">
      <div className="row">
        <h3 className="grow" style={{ margin: 0 }}>Interlocutory applications</h3>
        <button className="primary" onClick={() => setPicking(true)}>+ Add interlocutory</button>
      </div>

      {/* Rule 41A mediation banner */}
      <MediationCard
        matterId={matterId}
        referred={mediationReferredAt}
        resumed={mediationJointMinuteAt}
        active={mediationActive}
        onChange={() => { load(); onChange?.(); }}
      />

      {/* Children list */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 18 }} className="muted">Loading…</div>
        ) : children.length === 0 ? (
          <div style={{ padding: 18 }} className="muted">
            No interlocutory applications yet. Common: summary judgment, joinder,
            amendment, leave to appeal, taxation review, rescission.
          </div>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {children.map((c) => (
              <li key={String(c.ROWID)} style={row}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Link to={`/matters/${c.ROWID}`} style={{ fontWeight: 500, textDecoration: 'none' }}>
                    {c.matter_name}
                  </Link>
                  <div className="muted" style={{ fontSize: 12 }}>
                    {humanInterloc(c.interlocutory_type)} · opened {fmtDate(c.opened_at)}
                    {c.finalised_at ? ` · finalised ${fmtDate(c.finalised_at)}` : ''}
                  </div>
                </div>
                <StatusPill status={c.status} />
              </li>
            ))}
          </ul>
        )}
      </div>

      {picking && (
        <AddInterlocutoryModal
          matterId={matterId}
          parentMatterTypeCode={parentMatterTypeCode}
          onClose={() => setPicking(false)}
          onCreated={() => { setPicking(false); load(); onChange?.(); }}
        />
      )}
    </div>
  );
}

// ---- Mediation card ---------------------------------------------------------
function MediationCard({
  matterId, referred, resumed, active, onChange,
}: { matterId: number | string; referred?: string | null; resumed?: string | null; active: boolean; onChange: () => void }) {
  const [busy, setBusy] = useState(false);

  const refer = async () => {
    if (!confirm('Refer this matter to Rule 41A mediation? All pleadings deadlines will be paused until the joint minute is filed.')) return;
    setBusy(true);
    try { await matterApi.referMediation(matterId); onChange(); }
    finally { setBusy(false); }
  };
  const resume = async () => {
    if (!confirm('Mark Rule 41A joint minute filed and resume pleadings deadlines?')) return;
    setBusy(true);
    try { await matterApi.resumeMediation(matterId); onChange(); }
    finally { setBusy(false); }
  };

  if (active) {
    return (
      <div className="card" style={{ borderLeft: '3px solid var(--color-warning, #d97706)' }}>
        <div className="row">
          <div className="grow">
            <div style={{ fontWeight: 600 }}>Rule 41A mediation in progress</div>
            <div className="muted" style={{ fontSize: 12 }}>
              Referred {fmtDate(referred)}. All future pleadings deadlines suspended (Rule 41A(4)(c)).
              When the joint minute is filed (deemed within 30 days, Rule 41A(8)), resume the clock.
            </div>
          </div>
          <button onClick={resume} disabled={busy}>Joint minute filed — resume</button>
        </div>
      </div>
    );
  }
  return (
    <div className="card">
      <div className="row">
        <div className="grow">
          <div style={{ fontWeight: 500 }}>Rule 41A mediation</div>
          <div className="muted" style={{ fontSize: 12 }}>
            {referred && resumed
              ? `Last referral: ${fmtDate(referred)} · resumed ${fmtDate(resumed)}.`
              : 'Not referred. Referring suspends pleadings deadlines until the joint minute is filed.'}
          </div>
        </div>
        <button onClick={refer} disabled={busy}>Refer to mediation</button>
      </div>
    </div>
  );
}

// ---- Helpers ----------------------------------------------------------------
function StatusPill({ status }: { status: string }) {
  const cfg = {
    open:       { label: 'Open',       color: 'var(--color-info, #2563eb)' },
    on_hold:    { label: 'On hold',    color: 'var(--color-warning, #d97706)' },
    finalised:  { label: 'Finalised',  color: 'var(--color-success, #15803d)' },
    archived:   { label: 'Archived',   color: 'var(--color-text-muted, #888)' },
  }[status] || { label: status, color: 'var(--color-text-muted, #888)' };
  return (
    <span style={{
      fontSize: 11, padding: '3px 8px', borderRadius: 12,
      border: `1px solid ${cfg.color}`, color: cfg.color, whiteSpace: 'nowrap',
    }}>{cfg.label}</span>
  );
}

function humanInterloc(code: string) {
  return code.replace(/^interlocutory_/, '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function fmtDate(iso?: string | null) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return iso; }
}

const row: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 14,
  padding: '12px 18px', borderBottom: '1px solid var(--color-border)',
};
